import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import styles from './ActivityTimelineChart.module.scss';

const nf0 = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const nfCompact = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });

function n0(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return nf0.format(n);
}

function nCompact(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return nfCompact.format(n);
}

function formatTimeWithDate(iso, window) {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);

  const showDate = window && (window === '2d' || window === '3d' || window === '7d' || window === '30d');

  if (showDate) {
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const timeStr = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${month}/${day} ${timeStr}`;
  }

  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

function buildLinePath(points) {
  if (!points.length) return '';
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
}

export function ActivityTimelineChart({ buckets, height = 230, window: metricsWindow = '24h' }) {
  const [hoverIdx, setHoverIdx] = useState(null);

  const series = useMemo(() => {
    const safe = Array.isArray(buckets) ? buckets : [];
    const pts = safe.map((b) => ({
      ts: b.timestamp,
      calls: Number(b.calls || 0),
      tokens: Number(b.total_tokens || 0),
    }));

    const maxY = Math.max(...pts.map((p) => p.tokens), 1);
    const w = 1000;
    const h = 260;
    const left = 50;
    const right = 10;
    const top = 14;
    const bottom = 40;
    const plotW = w - left - right;
    const plotH = h - top - bottom;
    const xScale = (i) => left + (pts.length <= 1 ? 0 : (i / (pts.length - 1)) * plotW);
    const yScale = (val) => top + ((maxY - val) / maxY) * plotH;

    const tokenPts = pts.map((p, i) => ({ ...p, x: xScale(i), yTokens: yScale(p.tokens), yCalls: yScale(p.calls) }));
    const tokenPath = buildLinePath(tokenPts.map((p) => ({ x: p.x, y: p.yTokens })));
    const callsPath = buildLinePath(tokenPts.map((p) => ({ x: p.x, y: p.yCalls })));

    const areaPath = tokenPts.length
      ? `${tokenPath} L ${tokenPts[tokenPts.length - 1].x.toFixed(2)} ${220} L ${tokenPts[0].x.toFixed(2)} ${220} Z`
      : '';

    const tickCount = 5;
    const yTicks = Array.from({ length: tickCount }, (_, i) => {
      const value = (maxY / (tickCount - 1)) * i;
      return {
        value,
        y: yScale(value),
        label: nCompact(value),
      };
    });

    const showDate = metricsWindow && (metricsWindow === '2d' || metricsWindow === '3d' || metricsWindow === '7d' || metricsWindow === '30d');
    const maxTicks = showDate ? 5 : 6;
    const xTickCount = Math.min(maxTicks, tokenPts.length);
    const xTicks = [];
    if (tokenPts.length > 0) {
      for (let i = 0; i < xTickCount; i++) {
        const idx = tokenPts.length === 1 ? 0 : Math.round((i / (xTickCount - 1)) * (tokenPts.length - 1));
        const pt = tokenPts[idx];
        xTicks.push({
          x: pt.x,
          label: formatTimeWithDate(pt.ts, metricsWindow),
          idx,
        });
      }
    }

    return { tokenPts, tokenPath, callsPath, areaPath, maxY, yTicks, xTicks, view: { w, h, left, right, top, bottom } };
  }, [buckets, metricsWindow]);

  const handleMove = (e) => {
    const svg = e.currentTarget;
    const rect = svg.getBoundingClientRect();
    if (!rect.width || !series.tokenPts.length) return;
    const x = ((e.clientX - rect.left) / rect.width) * series.view.w;
    const { left, w, right } = series.view;
    const plotW = w - left - right;
    const t = plotW <= 0 ? 0 : (x - left) / plotW;
    const idx = Math.max(0, Math.min(series.tokenPts.length - 1, Math.round(t * (series.tokenPts.length - 1))));
    setHoverIdx(idx);
  };

  const handleLeave = () => setHoverIdx(null);
  const hp = hoverIdx === null ? null : series.tokenPts[hoverIdx];

  if (!series.tokenPts.length) {
    return (
      <Box className={styles.chartEmpty}>
        <Typography variant="body2" color="text.secondary">
          No trend data for this window.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={styles.chartWrap}>
      <Box className={styles.chartBox}>
        <svg
          className={styles.chartSvg}
          viewBox="0 0 1000 260"
          preserveAspectRatio="xMidYMid meet"
          height={height}
          onMouseMove={handleMove}
          onMouseEnter={handleMove}
          onMouseLeave={handleLeave}
          role="img"
          aria-label="Activity timeline chart"
        >
          <defs>
            <linearGradient id="tokensFillDash" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(56, 189, 248, 0.20)" />
              <stop offset="100%" stopColor="rgba(56, 189, 248, 0.00)" />
            </linearGradient>
          </defs>

          <line x1="50" y1="220" x2="990" y2="220" stroke="rgba(148,163,184,0.25)" strokeWidth="1" />
          <line x1="50" y1="14" x2="50" y2="220" stroke="rgba(148,163,184,0.25)" strokeWidth="1" />

          {series.yTicks.map((tick, idx) => (
            <g key={idx}>
              <line x1="45" y1={tick.y} x2="50" y2={tick.y} stroke="rgba(148,163,184,0.25)" strokeWidth="1" />
              <text
                x="42"
                y={tick.y + 3}
                fill="var(--text-secondary)"
                fontSize="9"
                fontWeight="500"
                textAnchor="end"
                style={{ fontFamily: 'inherit' }}
              >
                {tick.label}
              </text>
            </g>
          ))}

          <text
            x="22"
            y="122"
            fill="var(--text-secondary)"
            fontSize="11"
            fontWeight="700"
            textAnchor="middle"
            transform="rotate(-90, 22, 122)"
            style={{ fontFamily: 'inherit' }}
          >
            Tokens
          </text>

          <text
            x="22"
            y="198"
            fill="rgba(16, 185, 129, 0.9)"
            fontSize="10"
            fontWeight="700"
            textAnchor="middle"
            transform="rotate(-90, 22, 198)"
            style={{ fontFamily: 'inherit' }}
          >
            API Calls
          </text>

          {series.xTicks.map((tick, idx) => (
            <g key={idx}>
              <line x1={tick.x} y1="220" x2={tick.x} y2="225" stroke="rgba(148,163,184,0.25)" strokeWidth="1" />
              <text
                x={tick.x}
                y="235"
                fill="var(--text-secondary)"
                fontSize="9"
                fontWeight="500"
                textAnchor="middle"
                style={{ fontFamily: 'inherit' }}
              >
                {tick.label}
              </text>
            </g>
          ))}

          <text
            x="520"
            y="252"
            fill="var(--text-secondary)"
            fontSize="11"
            fontWeight="700"
            textAnchor="middle"
            style={{ fontFamily: 'inherit' }}
          >
            Time
          </text>

          <path d={series.areaPath} fill="url(#tokensFillDash)" />
          <path d={series.tokenPath} fill="none" stroke="var(--primary)" strokeWidth="2.5" />
          <path d={series.callsPath} fill="none" stroke="rgba(16, 185, 129, 0.9)" strokeWidth="2" />

          {hp && (
            <>
              <line x1={hp.x} y1="14" x2={hp.x} y2="220" stroke="rgba(148,163,184,0.25)" strokeWidth="1" />
              <circle cx={hp.x} cy={hp.yTokens} r="5.5" fill="var(--primary)" stroke="white" strokeWidth="2" />
            </>
          )}

          {series.tokenPts.map((p, idx) => (
            <circle
              key={idx}
              cx={p.x}
              cy={p.yTokens}
              r={hoverIdx === idx ? 4.5 : 3.2}
              fill={hoverIdx === idx ? 'var(--primary)' : 'rgba(227, 98, 80, 0.45)'}
              stroke="rgba(255,255,255,0.85)"
              strokeWidth="1"
            />
          ))}
        </svg>

        {hp && (
          <Box className={styles.chartTooltip} sx={{ left: `${Math.min(Math.max((hp.x / 1000) * 100, 10), 90)}%` }}>
            <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
              Time: {formatTimeWithDate(hp.ts, metricsWindow)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
              Calls: {n0(hp.calls)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Tokens: {n0(hp.tokens)}
            </Typography>
          </Box>
        )}
      </Box>

      <Box className={styles.chartLegend}>
        <Box className={styles.legendItem}>
          <span className={styles.dotCalls} /> API Calls
        </Box>
        <Box className={styles.legendItem}>
          <span className={styles.dotTokens} /> Tokens
        </Box>
      </Box>
    </Box>
  );
}
